import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { Phone, Mail, Send } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const inputCls =
  "w-full rounded border border-white/10 bg-slate-900/50 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-600 outline-none transition-colors duration-300 focus:border-[#00e5ff]/60";

export const Contact = ({ t }) => {
  const [form, setForm] = useState({ name: "", email: "", company: "", message: "" });
  const [sending, setSending] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success(t.success);
      setForm({ name: "", email: "", company: "", message: "" });
    } catch {
      toast.error(t.error);
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" className="relative overflow-hidden border-t border-white/10" data-testid="contact-section">
      <div className="pointer-events-none absolute -bottom-40 left-[-10%] h-[60vh] w-[60vh] rounded-full bg-[#00e5ff]/10 blur-[140px]" />
      <div className="relative mx-auto grid max-w-[1440px] gap-16 px-5 py-28 md:px-10 md:py-40 lg:grid-cols-2">
        <div>
          <motion.p
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-6 font-mono-spec text-[11px] uppercase tracking-[0.3em] text-[#00e5ff]"
            data-testid="contact-kicker"
          >
            / {t.kicker}
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
            className="font-display text-3xl font-semibold leading-tight tracking-tight md:text-5xl"
            data-testid="contact-title"
          >
            {t.title}
          </motion.h2>
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 1, delay: 0.2 }}
            className="mt-12 space-y-6"
          >
            <div>
              <p className="font-mono-spec text-[10px] uppercase tracking-[0.3em] text-slate-500">{t.callUs}</p>
              <a href="tel:+40775160822" data-testid="contact-phone" className="mt-1 block font-display text-xl text-slate-100 transition-colors duration-300 hover:text-[#00e5ff] md:text-2xl">
                <Phone className="mr-3 inline h-5 w-5 text-[#00e5ff]" strokeWidth={1.5} />
                +40 775 160 822
              </a>
            </div>
            <div>
              <p className="font-mono-spec text-[10px] uppercase tracking-[0.3em] text-slate-500">{t.writeUs}</p>
              <a href="mailto:ab@sqbrobotics.com" data-testid="contact-email" className="mt-1 block font-display text-xl text-slate-100 transition-colors duration-300 hover:text-[#00e5ff] md:text-2xl">
                <Mail className="mr-3 inline h-5 w-5 text-[#00e5ff]" strokeWidth={1.5} />
                ab@sqbrobotics.com
              </a>
            </div>
          </motion.div>
        </div>

        <motion.form
          initial={{ opacity: 0, y: 48 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 1, delay: 0.15, ease: [0.16, 1, 0.3, 1] }}
          onSubmit={submit}
          className="clipped-frame h-fit bg-slate-950/60 p-7 backdrop-blur-xl md:p-10"
          data-testid="contact-form"
        >
          <div className="grid gap-5 sm:grid-cols-2">
            <div>
              <label className="mb-2 block font-mono-spec text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.name}</label>
              <input required value={form.name} onChange={set("name")} className={inputCls} data-testid="contact-name-input" />
            </div>
            <div>
              <label className="mb-2 block font-mono-spec text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.email}</label>
              <input required type="email" value={form.email} onChange={set("email")} className={inputCls} data-testid="contact-email-input" />
            </div>
          </div>
          <div className="mt-5">
            <label className="mb-2 block font-mono-spec text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.company}</label>
            <input value={form.company} onChange={set("company")} className={inputCls} data-testid="contact-company-input" />
          </div>
          <div className="mt-5">
            <label className="mb-2 block font-mono-spec text-[10px] uppercase tracking-[0.25em] text-slate-500">{t.message}</label>
            <textarea required rows={5} value={form.message} onChange={set("message")} placeholder={t.messagePh} className={`${inputCls} resize-none`} data-testid="contact-message-input" />
          </div>
          <button
            type="submit"
            disabled={sending}
            data-testid="contact-submit-btn"
            className="group mt-8 flex w-full items-center justify-center gap-3 rounded-full bg-[#00e5ff] px-7 py-4 font-mono-spec text-xs uppercase tracking-[0.25em] text-slate-950 transition-all duration-300 hover:-translate-y-1 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {sending ? t.sending : t.submit}
            <Send className="h-4 w-4 transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </motion.form>
      </div>
    </section>
  );
};
